import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { TabAccordion } from 'components/Forms/TabAccordion';
import Loader from 'components/Loader';
import { LoadError } from 'components/LoadError';
import { has_permission } from 'config/permissions';
import { useAccordionTab } from 'helpers/useAccordionTab';
import { useAdminAuth } from 'hooks/useAdminAuth';
import { useGetClientFiltersQuery } from 'services/clientFiltersApi';
import {
  FILTERS_LOAD_ERROR,
  HDD_CLIENT_ID,
  RECIPIENTS_LOAD_ERROR,
  getNoCartNotice,
} from '../constants';
import { FilterAccordion } from './FilterAccordion';
import { FilterDetailMessage } from './FilterDetailMessage';
import { FilterFormValues } from './types';
import { useFilterRecipientOptions } from './useFilterRecipientOptions';
import { useFilterSubmit } from './useFilterSubmit';
import { toInitialValues } from './utils';
import {
  FILTER_CHECKS_TAB,
  FILTER_NOT_FOUND,
  FILTER_PAGE_TYPE,
  FILTER_SECTIONS,
} from './constants';

const FilterDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const filterId = new URLSearchParams(location.search).get('filter_id');
  const { clientId, activeRole, hasClient } = useAdminAuth();
  const canViewFilter = has_permission('FILTERS_DETAIL_ROUTE', 'R', activeRole);
  // HDD shows Oath's filters on the site, so its own filters can't be edited.
  const canEditClient = hasClient && clientId !== HDD_CLIENT_ID;
  const filtersClientId = clientId ?? 0;

  const [tabStatus, setTabStatus] = useState<string[]>(['in-progress', '']);
  const updateStatus = (item: number, status: string) => {
    setTabStatus(prev => prev.map((s, index) => (index === item ? status : s)));
  };
  const { handleClickAccordion, tabIndexVisible } = useAccordionTab({
    updateStatus,
    tabStatus,
    pageType: FILTER_PAGE_TYPE,
    checksTab: FILTER_CHECKS_TAB,
  });

  const {
    currentData: filtersData,
    isError: isFiltersError,
    refetch: refetchFilters,
  } = useGetClientFiltersQuery(
    { clientId: filtersClientId },
    { skip: !canViewFilter || !canEditClient || !filterId }
  );
  const filter = filtersData?.filters.find(({ filter_id }) => filter_id === filterId);
  const isFilterPending = Boolean(filterId) && !filtersData;

  const {
    options,
    toRecipientOption,
    showsCriteriaNote,
    isLoading: isOptionsLoading,
    hasError: hasOptionsError,
    retry: retryOptions,
    hasNoCart,
    clientName,
  } = useFilterRecipientOptions(filtersClientId, filter?.recipient_ids ?? [], {
    skip: !canViewFilter || !canEditClient || isFilterPending,
  });

  // Built once the labels are final; initialValuesEqual below keeps a later refetch from resetting edits.
  const initialValues = useMemo(
    () =>
      isFilterPending || isOptionsLoading ? undefined : toInitialValues(filter, toRecipientOption),
    [isFilterPending, isOptionsLoading, filter, toRecipientOption]
  );

  useEffect(() => {
    if (canViewFilter && !canEditClient) {
      navigate('/filters', { replace: true });
    }
  }, [canViewFilter, canEditClient, navigate]);

  const handleSubmit = useFilterSubmit(filtersClientId, filter);

  if (!activeRole) return null;

  if (!canViewFilter) {
    return (
      <div className="content-full-width">
        <h1 className="title-permission">
          Sorry, you don&apos;t have permission to see this screen.
        </h1>
      </div>
    );
  }

  if (!canEditClient) return null;

  if (isFiltersError) {
    return (
      <FilterDetailMessage isNew={!filterId}>
        <LoadError message={FILTERS_LOAD_ERROR} onRetry={refetchFilters} />
      </FilterDetailMessage>
    );
  }

  if (hasNoCart) {
    return (
      <FilterDetailMessage isNew={!filterId}>
        <span className="oath-body">{getNoCartNotice(clientName)}</span>
      </FilterDetailMessage>
    );
  }

  if (hasOptionsError) {
    return (
      <FilterDetailMessage isNew={!filterId}>
        <LoadError message={RECIPIENTS_LOAD_ERROR} onRetry={retryOptions} />
      </FilterDetailMessage>
    );
  }

  if (filterId && filtersData && !filter) {
    return (
      <FilterDetailMessage isNew={false}>
        <span className="oath-body">{FILTER_NOT_FOUND}</span>
      </FilterDetailMessage>
    );
  }

  if (!initialValues) return <Loader type="dots" />;

  return (
    <div className="content-full-width">
      {/* Keyed by client: the navbar picker can switch clients without unmounting this page. */}
      <Form<FilterFormValues>
        key={filtersClientId}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        initialValuesEqual={() => true}
        render={({ handleSubmit: submitForm, form, submitting }) => (
          <form onSubmit={submitForm} className="detail-form">
            <DetailHeader saveForm={submitForm} type="Filter" isNew={!filter} backTo="/filters" />
            {FILTER_SECTIONS.map((type, index) => (
              <div key={type}>
                <TabAccordion
                  index={index}
                  itemVisible={tabIndexVisible}
                  type={type}
                  tabStatus={tabStatus}
                  handleClickAccordion={() => handleClickAccordion(index, form, 'tab')}
                />
                <FilterAccordion
                  index={index}
                  itemVisible={tabIndexVisible}
                  savedName={filter?.filter_name}
                  recipientOptions={options}
                  showsCriteriaNote={showsCriteriaNote}
                  isSubmitting={submitting}
                  onNext={() => handleClickAccordion(index + 1, form, 'next')}
                />
              </div>
            ))}
          </form>
        )}
      />
    </div>
  );
};

export default FilterDetail;
